import { useAuth0 } from '@auth0/auth0-react';
import * as LucideIcons from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Select from 'react-select';
import { useDataReload } from '../renderer/DataReloadContext';
import './ModalForm.css';
import { showToast } from './ToastHandler';

const ModalForm = ({ fields, onSubmit, submitAction, rowData }) => {
  const { getIdTokenClaims } = useAuth0();
  const { reloadData } = useDataReload();
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  // Initialize form data with default values
  useEffect(() => {
    const initialData = {};
    fields.forEach(field => {
      if (field.default !== undefined) {
        initialData[field.key] = field.default;
      }
    });
    setFormData(initialData);
  }, [fields]);

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = await getIdTokenClaims();
      const headers = {
        'Content-Type': 'application/json',
        ...JSON.parse(submitAction.headers || '{}'),
        Authorization: `Bearer ${token.__raw}`,
      };

      // Interpolate form values into body template (only if body is a string)
      let body = submitAction.body;

      // Helper function to escape regex metacharacters
      const escapeRegex = string => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      };

      if (typeof body === 'string') {
        // Try to parse as JSON to handle proper data type preservation
        try {
          const bodyObj = JSON.parse(body);

          // Check if body uses placeholder syntax like {fieldName}
          const hasPlaceholders = Object.keys(formData).some(key =>
            body.includes(`{${key}}`)
          );

          if (hasPlaceholders) {
            // Handle placeholder-based JSON templates
            Object.keys(formData).forEach(key => {
              const escapedKey = escapeRegex(key);
              const placeholder = `{${key}}`;
              const quotedPlaceholder = `"${placeholder}"`;

              // Use JSON.stringify for consistent data type handling in JSON
              const jsonValue = JSON.stringify(formData[key]);

              // Replace quoted placeholders first, then unquoted ones
              if (body.includes(quotedPlaceholder)) {
                body = body.replace(
                  new RegExp(`"\\{${escapedKey}\\}"`, 'g'),
                  jsonValue
                );
              } else if (body.includes(placeholder)) {
                body = body.replace(
                  new RegExp(`\\{${escapedKey}\\}`, 'g'),
                  jsonValue
                );
              }
            });
          } else {
            // Handle property-based JSON templates
            Object.keys(formData).forEach(key => {
              if (Object.prototype.hasOwnProperty.call(bodyObj, key)) {
                bodyObj[key] = formData[key]; // Direct assignment preserves data types
              }
            });
            body = JSON.stringify(bodyObj);
          }
        } catch (e) {
          // Fallback to string replacement for non-JSON bodies (stringify all values)
          Object.keys(formData).forEach(key => {
            const escapedKey = escapeRegex(key);
            const value = JSON.stringify(formData[key]); // Stringify all values for backward compatibility
            body = body.replace(new RegExp(`\\{${escapedKey}\\}`, 'g'), value);
          });
        }
      } else {
        // No body template provided (e.g., GET requests) -> avoid sending a body
        body = undefined;
      }
      // Process URL placeholders using form data (e.g., ...?ksc_id={ksc_id})
      let processedUrl = submitAction.url;
      if (typeof processedUrl === 'string') {
        try {
          Object.keys(formData).forEach(key => {
            const escapedKey = escapeRegex(key);
            const placeholder = `{${key}}`;
            if (processedUrl.includes(placeholder)) {
              const rawVal = formData[key];
              const asString =
                typeof rawVal === 'string' ? rawVal : JSON.stringify(rawVal);
              const encoded = encodeURIComponent(asString ?? '');
              processedUrl = processedUrl.replace(
                new RegExp(`\\{${escapedKey}\\}`, 'g'),
                encoded
              );
            }
          });

          // Remove any query params with empty values (e.g., integration_id=)
          if (processedUrl.includes('?')) {
            const isAbsolute = /^https?:\/\//i.test(processedUrl);
            const urlObj = new URL(
              processedUrl,
              isAbsolute ? undefined : window.location.origin
            );
            const params = new URLSearchParams(urlObj.search);
            const cleaned = new URLSearchParams();
            params.forEach((val, key) => {
              // Drop empty strings, "" (from JSON.stringify), null/undefined strings
              if (
                val !== '' &&
                val !== '""' &&
                val.toLowerCase() !== 'null' &&
                val.toLowerCase() !== 'undefined'
              ) {
                cleaned.append(key, val);
              }
            });
            const basePath =
              urlObj.pathname +
              (cleaned.toString() ? `?${cleaned.toString()}` : '');
            processedUrl = isAbsolute
              ? `${urlObj.origin}${basePath}${urlObj.hash || ''}`
              : `${basePath}${urlObj.hash || ''}`;
          }
        } catch (e) {
          // Fallback: leave URL unchanged if anything goes wrong
        }
      }

      // Process URL to handle user_id replacements if needed (legacy)
      if (processedUrl.includes('{user_id}')) {
        // This is a fallback, but the URL should already be processed by ActionCell
        console.warn(
          'URL still contains {user_id} placeholder, this should be handled by ActionCell'
        );
      }

      const response = await fetch(processedUrl, {
        method: submitAction.method,
        headers,
        body,
      });

      // Check if error is 503 Service Unavailable for refresh_tabs endpoint
      if (
        response.status === 503 &&
        submitAction.url.includes('/v3/deal_room/refresh_tabs')
      ) {
        showToast(
          'info',
          'Your request is being processed in the background. Please wait a moment and refresh to see updates.'
        );

        // Reset form data and close modal
        setFormData({});
        onSubmit(formData);

        return; // Exit early to avoid further processing
      }

      // Handle file downloads for successful responses
      if (response.ok) {
        const contentType = response.headers.get('Content-Type');

        // First, try to parse as JSON to check for presigned_url
        try {
          const responseData = await response.json();

          // Custom: Integrations create flow returns {redirect_url}
          if (
            responseData &&
            responseData.redirect_url &&
            (location.pathname.startsWith('/integrations') ||
              (submitAction.url || '').includes('/v3/integrations/oauth_url'))
          ) {
            // Redirect the user to the provided URL
            window.location.href = responseData.redirect_url;
            onSubmit(formData);
            if (submitAction.response.clear_form) {
              setFormData({});
            }
            return;
          }

          // Check if we got a presigned_url in the response
          if (responseData.presigned_url) {
            // Try to force download using a link with download attribute
            const link = document.createElement('a');
            link.href = responseData.presigned_url;
            link.download = responseData.filename || 'download';
            link.style.display = 'none';
            link.target = '_blank';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Show helpful message to user
            setTimeout(() => {
              showToast(
                'info',
                `If the file opened in your browser instead of downloading, right-click on the page and select "Save As" to save "${responseData.filename || 'file'}" to your computer.`,
                8000 // Show for 8 seconds
              );
            }, 2000); // Show after 2 seconds to give download a chance

            showToast(
              'success',
              `Download initiated for "${responseData.filename || 'file'}"`
            );
            onSubmit(formData);
            if (submitAction.response.clear_form) {
              setFormData({});
            }
            return;
          }

          // If JSON response but no presigned_url, continue with normal processing
          // (This will be handled by the existing handler logic below)
        } catch (jsonError) {
          // Response is not JSON, check if it's a direct file download
          if (contentType && !contentType.includes('application/json')) {
            // Reset response stream and treat as direct file data
            const response2 = await fetch(processedUrl, {
              method: submitAction.method,
              headers,
              body,
            });

            const blob = await response2.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // Try to get filename from Content-Disposition header
            const contentDisposition = response2.headers.get(
              'Content-Disposition'
            );
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename="(.+)"/);
              if (filenameMatch) {
                link.download = filenameMatch[1];
              }
            }

            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            showToast('success', 'File downloaded successfully');
            onSubmit(formData);
            if (submitAction.response.clear_form) {
              setFormData({});
            }
            return;
          }
        }
      }

      const handler =
        submitAction.response.status_handlers.find(
          h => h.status_code === response.status
        ) || submitAction.response.default_handler;

      if (handler.show_toast) {
        showToast(handler.toast_type, handler.message);
      }

      if (response.ok) {
        onSubmit(formData);
        if (submitAction.response.clear_form) {
          setFormData({});
        }

        // Check for refresh/reload at the response level
        if (submitAction.response.refresh_page) {
          if (location.pathname.startsWith('/response/')) {
            window.location.reload();
          } else {
            reloadData();
          }
        } else if (submitAction.response.reload_data) {
          if (location.pathname.startsWith('/response/')) {
            // For response details, prefer full reload to update stateful UI
            window.location.reload();
          } else {
            reloadData();
          }
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      showToast('error', 'Action failed. Please try again.');
      // Handle error case
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Function to evaluate visibility condition
  const shouldShowField = field => {
    if (!field.visibility_condition) {
      return true; // Show field if no condition is specified
    }

    const { depends_on, show_if } = field.visibility_condition;
    const dependentValue = formData[depends_on];

    if (show_if.hasOwnProperty('equals')) {
      return dependentValue === show_if.equals;
    }

    // Future: can extend with other conditions like 'not_equals', 'in', 'not_in', etc.
    return true;
  };

  // Function to filter options based on existing values in rowData
  const getFilteredOptions = field => {
    if (!field.options || !rowData) {
      return field.options || [];
    }

    // Only apply role filtering when on /users page
    if (!location.pathname.includes('/users')) {
      return field.options;
    }

    // For role management fields, check if we need to exclude existing roles
    if (field.key === 'groups' && rowData.groups) {
      // Get existing roles from rowData.groups (could be array or string)
      let existingRoles = [];
      if (Array.isArray(rowData.groups)) {
        // rowData.groups is already an array, use it directly
        existingRoles = rowData.groups;
      } else if (typeof rowData.groups === 'string') {
        // Check if it's a JSON string that represents an array
        if (rowData.groups.startsWith('[') && rowData.groups.endsWith(']')) {
          try {
            existingRoles = JSON.parse(rowData.groups);
          } catch (error) {
            console.error('Failed to parse JSON array:', error);
            existingRoles = [];
          }
        } else {
          // Handle comma-separated string or single role
          existingRoles = rowData.groups.split(',').map(role => role.trim());
        }
      }

      // Filter out existing roles from the options
      return field.options.filter(
        option => !existingRoles.includes(option.value)
      );
    }

    if (field.key === 'role' && rowData.groups) {
      // For single role removal, only show existing roles
      let existingRoles = [];
      if (Array.isArray(rowData.groups)) {
        // rowData.groups is already an array, use it directly
        existingRoles = rowData.groups;
      } else if (typeof rowData.groups === 'string') {
        // Check if it's a JSON string that represents an array
        if (rowData.groups.startsWith('[') && rowData.groups.endsWith(']')) {
          try {
            existingRoles = JSON.parse(rowData.groups);
          } catch (error) {
            console.error('Failed to parse JSON array:', error);
            existingRoles = [];
          }
        } else {
          // Handle comma-separated string or single role
          existingRoles = rowData.groups.split(',').map(role => role.trim());
        }
      }

      // Only show roles that the user currently has
      return field.options.filter(option =>
        existingRoles.includes(option.value)
      );
    }

    return field.options;
  };

  const renderFormField = field => {
    // Helper function to get current field value with default fallback
    const getCurrentValue = (fieldKey, defaultValue = '') => {
      return formData[fieldKey] !== undefined
        ? formData[fieldKey]
        : field.default !== undefined
          ? field.default
          : defaultValue;
    };

    switch (field.type) {
      case 'boolean':
        return (
          <Form.Check
            type="checkbox"
            label={field.placeholder || 'Enable this option'}
            checked={getCurrentValue(field.key, false)}
            onChange={e => handleInputChange(field.key, e.target.checked)}
            required={field.required}
          />
        );
      case 'radio':
        const currentRadioValue = getCurrentValue(field.key);
        return (
          <div>
            {field.options.map((option, index) => (
              <Form.Check
                key={index}
                type="radio"
                id={`${field.key}-${option.value}`}
                name={field.key}
                label={option.label}
                value={option.value}
                checked={currentRadioValue === option.value}
                onChange={e => handleInputChange(field.key, e.target.value)}
                required={field.required}
                className="mb-2"
              />
            ))}
          </div>
        );
      case 'single_select':
        const filteredOptions = getFilteredOptions(field);
        const selectOptions = filteredOptions.map(option => ({
          value: option.value,
          label: option.label,
        }));

        const currentSelectValue = getCurrentValue(field.key);
        const selectedOption =
          selectOptions.find(option => option.value === currentSelectValue) ||
          null;

        return (
          <Select
            options={selectOptions}
            value={selectedOption}
            onChange={selectedOption =>
              handleInputChange(
                field.key,
                selectedOption ? selectedOption.value : ''
              )
            }
            placeholder={field.placeholder}
            isClearable
            isSearchable
            required={field.required}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            styles={{
              control: (provided, state) => ({
                ...provided,
                borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                boxShadow: state.isFocused
                  ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                  : 'none',
                '&:hover': {
                  borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                },
              }),
              menu: provided => ({
                ...provided,
                zIndex: 9999,
              }),
              menuPortal: provided => ({
                ...provided,
                zIndex: 9999,
              }),
            }}
            classNamePrefix="react-select"
          />
        );
      case 'multi_select':
        const filteredMultiOptions = getFilteredOptions(field);
        const multiSelectOptions = filteredMultiOptions.map(option => ({
          value: option.value,
          label: option.label,
        }));

        const currentMultiValue = getCurrentValue(field.key, []);
        const selectedMultiOptions = Array.isArray(currentMultiValue)
          ? multiSelectOptions.filter(option =>
              currentMultiValue.includes(option.value)
            )
          : [];

        return (
          <Select
            options={multiSelectOptions}
            value={selectedMultiOptions}
            onChange={selectedOptions => {
              const values = selectedOptions
                ? selectedOptions.map(option => option.value)
                : [];
              handleInputChange(field.key, values);
            }}
            placeholder={field.placeholder}
            isClearable
            isSearchable
            isMulti
            required={field.required}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            styles={{
              control: (provided, state) => ({
                ...provided,
                borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                boxShadow: state.isFocused
                  ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)'
                  : 'none',
                '&:hover': {
                  borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
                },
              }),
              menu: provided => ({
                ...provided,
                zIndex: 9999,
              }),
              menuPortal: provided => ({
                ...provided,
                zIndex: 9999,
              }),
            }}
            classNamePrefix="react-select"
          />
        );
      case 'long_text':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={field.placeholder}
            required={field.required}
            onChange={e => handleInputChange(field.key, e.target.value)}
            value={getCurrentValue(field.key, '')}
          />
        );
      case 'email':
        return (
          <Form.Control
            type="email"
            placeholder={field.placeholder}
            required={field.required}
            onChange={e => handleInputChange(field.key, e.target.value)}
            value={getCurrentValue(field.key, '')}
          />
        );
      default:
        return (
          <Form.Control
            type={field.type || 'text'}
            placeholder={field.placeholder}
            required={field.required}
            onChange={e => handleInputChange(field.key, e.target.value)}
            value={getCurrentValue(field.key, '')}
          />
        );
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {fields.map(
        field =>
          shouldShowField(field) && (
            <Form.Group key={field.key} className="mb-3">
              <Form.Label>{field.label}</Form.Label>
              {renderFormField(field)}
            </Form.Group>
          )
      )}
      <Button type="submit" variant="primary" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <LucideIcons.Loader2 size={16} className="animate-spin" />
            <span style={{ marginLeft: '8px' }}>Submitting...</span>
          </>
        ) : (
          'Submit'
        )}
      </Button>
    </Form>
  );
};

export default ModalForm;
